import React, { useState, useRef, useContext } from 'react';
import { SimpleStorageContext } from '../../context/simpleStorage/simpleStorageContext';
import Swal from 'sweetalert2';

function SimpleStorageImagesForm({ idCasa, onUploadComplete, entityCode }) {
    const [isDragging, setIsDragging] = useState(false);
    const [previews, setPreviews] = useState([]);
    const fileInputRef = useRef(null);

    const { uploadImages, loading } = useContext(SimpleStorageContext);

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.target.id === 'imageDropzone') {
            setIsDragging(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    };

    const handleDropzoneClick = () => {
        fileInputRef.current.click();
    };

    const handleFileInputChange = (e) => {
        const files = Array.from(e.target.files);
        handleFiles(files);
    };

    const handleFiles = (files) => {
        const validFiles = files.filter(file => {
            const isImage = file.type.startsWith('image/');
            const isValidSize = file.size <= 10 * 1024 * 1024;

            if (!isImage) {
                Swal.fire('Error', `${file.name} no es una imagen válida`, 'error');
                return false;
            }
            if (!isValidSize) {
                Swal.fire('Error', `${file.name} excede el tamaño máximo de 10MB`, 'error');
                return false;
            }
            return true;
        });

        if (validFiles.length > 0) {
            generatePreviews(validFiles);
        }
    };

    const generatePreviews = (files) => {
        const previewPromises = files.map(file => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    resolve({
                        file,
                        preview: e.target.result,
                        name: file.name
                    });
                };
                reader.readAsDataURL(file);
            });
        });

        Promise.all(previewPromises).then(newPreviews => {
            setPreviews(prev => [...prev, ...newPreviews]);
        });
    };

    const handleRemovePreview = (index) => {
        setPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (previews.length === 0) {
            Swal.fire('Atención', 'Debes seleccionar al menos una imagen', 'warning');
            return;
        }

        if (!idCasa) {
            Swal.fire('Error', 'ID de propiedad no válido', 'error');
            return;
        }

        const formData = new FormData();
        formData.append('idCasa', parseInt(idCasa));
        formData.append('entityCode', entityCode);

        previews.forEach(preview => {
            formData.append('imagenes', preview.file);
        });

        try {
            await uploadImages(formData);
            Swal.fire('Listo', 'Imágenes subidas correctamente', 'success');
            setPreviews([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            if (onUploadComplete) {
                onUploadComplete();
            }
        } catch (error) {
            Swal.fire('Error', 'Hubo un problema al subir las imágenes', 'error');
        }
    };

    return (
        <div>
            <div
                id='imageDropzone'
                className={`border border-2 rounded p-4 text-center ${
                    isDragging
                        ? 'border-primary bg-primary bg-opacity-10'
                        : 'border-secondary bg-opacity-10'
                }`}
                style={{ cursor: 'pointer' }}
                onClick={handleDropzoneClick}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <p className='mb-1 fw-semibold'>Arrastrá imágenes acá</p>
                <p className='text-muted mb-0'>o hacé click para seleccionarlas</p>

                <input
                    ref={fileInputRef}
                    type='file'
                    id='imageInput'
                    accept='image/*'
                    multiple
                    hidden
                    onChange={handleFileInputChange}
                />
            </div>

            {previews.length > 0 && (
                <div className='mt-3'>
                    <div className='row g-2'>
                        {previews.map((preview, index) => (
                            <div key={index} className='col-6 col-md-4'>
                                <div className='position-relative'>
                                    <img
                                        src={preview.preview}
                                        alt={preview.name}
                                        className='img-fluid rounded'
                                        style={{ width: '100%', height: '100px', objectFit: 'cover' }}
                                    />
                                    <button
                                        type='button'
                                        className='btn btn-danger btn-sm position-absolute top-0 end-0 m-1'
                                        onClick={() => handleRemovePreview(index)}
                                        style={{ padding: '2px 6px', fontSize: '12px' }}
                                    >
                                        ✕
                                    </button>
                                    <small className='d-block text-truncate text-muted mt-1'>
                                        {preview.name}
                                    </small>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        type='button'
                        className='btn btn-primary btn-block mt-3 w-100'
                        onClick={handleUpload}
                        disabled={loading}
                    >
                        {loading ? 'Subiendo...' : `Subir ${previews.length} imagen${previews.length > 1 ? 'es' : ''}`}
                    </button>
                </div>
            )}
        </div>
    );
}

export default SimpleStorageImagesForm;
